import { message } from 'antd';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWith409 from './baseQueryWith409';
import qs from 'qs';
import fetch from 'auth/FetchInterceptor';

// direct fetch without RTK Query
export const getCandidatos = query =>
	fetch.get(
		`/candidatos${
			query
				? `?${qs.stringify(
						{ ...query, publicationState: 'live' },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);

export const searchCandidatos = query =>
	fetch.get(
		`/candidatos-custom/search${
			query
				? `?${qs.stringify(query, {
						encodeValuesOnly: true,
				  })}`
				: ''
		}`
	);

export const getCandidato = ({ id, query }) =>
	fetch.get(
		`/candidatos/${id}${
			query
				? `?${qs.stringify(
						{ ...query, publicationState: 'live' },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);
export const addCandidato = data => fetch.post('/candidatos', data);
export const updateCandidato = ({ id, ...data }) =>
	fetch.put(`/candidatos/${id}`, data);
export const deleteCandidato = id => fetch.delete(`/candidatos/${id}`);

// RTK Query
export const candidatosApi = createApi({
	reducerPath: 'candidatosApi',
	baseQuery: baseQueryWith409,

	// default configuration
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: true,
	refetchOnFocus: true,

	//endpoints
	endpoints: build => ({
		getCandidatos: build.query({
			query: query => {
				return {
					url: `/candidatos${
						query
							? `?${qs.stringify(
									{ ...query, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			transformResponse: response => response.data,
			providesTags: result =>
				// is result available?
				result
					? // successful query
					  [
							...result.map(({ id }) => ({
								type: 'Candidatos',
								id,
							})),
							{ type: 'Candidatos', id: 'LIST' },
					  ]
					: // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
					  [{ type: 'Candidatos', id: 'LIST' }],
		}),
		getCandidato: build.query({
			query: ({ id, query }) => {
				return {
					url: `/candidatos/${id}${
						query
							? `?${qs.stringify(
									{ ...query, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			transformResponse: response => response.data,
			providesTags: (result, error, id) => [{ type: 'Candidatos', id }],
		}),
		addCandidato: build.mutation({
			query: data => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/candidatos`,
					method: 'POST',
					body: JSON.stringify({
						data: {
							...data,
							created_by_user: user.id,
						},
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Candidato agregado exitosamente');
				} catch (error) {
					message.error('Error al agregar candidato');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: [{ type: 'Candidatos', id: 'LIST' }],
		}),
		updateCandidato: build.mutation({
			query: ({ id, ...data }) => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/candidatos/${id}`,
					method: 'PUT',
					body: JSON.stringify({
						data: {
							...data,
							updated_by_user: user.id,
						},
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Candidato actualizado exitosamente');
				} catch (error) {
					message.error('Error al actualizar candidato');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: (result, error, { id }) => [
				{ type: 'Candidatos', id },
			],
		}),
		deleteCandidato: build.mutation({
			query: id => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/candidatos/${id}`,
					method: 'PUT',
					body: JSON.stringify({
						data: {
							publishedAt: null,
							updated_by_user: user.id,
						},
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Candidato eliminado exitosamente');
				} catch (error) {
					message.error('Error al eliminar candidato');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: (result, error, id) => [{ type: 'Candidatos', id }],
		}),
		deleteCandidatoPermanente: build.mutation({
			query: id => ({
				url: `/candidatos/${id}/delete`,
				method: 'DELETE',
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					message.success('Candidato dado de baja exitosamente');
				} catch (error) {
					message.error('Error al dar de baja candidato');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: [{ type: 'Candidatos', id: 'LIST' }],
		}),
	}),
});

export const {
	useGetCandidatosQuery,
	useLazyGetCandidatosQuery,
	useGetCandidatoQuery,
	useAddCandidatoMutation,
	useUpdateCandidatoMutation,
	useDeleteCandidatoMutation,
	useDeleteCandidatoPermanenteMutation,
} = candidatosApi;
